import React from 'react';
import { node, shape, string, bool } from 'prop-types';
import { withStyles } from 'material-ui/styles';
import ArrowLeft from 'crehana-icons/NewArrowLeft';
import ArrowRight from 'crehana-icons/NewArrowRight';
import classNames from 'classnames';
import {
  mainText,
  mainColor,
  gray2,
  white as whiteColor,
} from '../styles/colors';
import { reduceClasses } from '../utils/testUtils';

export const styles = ({ breakpoints }) => {
  const baseStyles = {
    width: '48px',
    height: '48px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    transition: 'border 0.3s ease-in-out',
    cursor: 'pointer',
    '&:focus': {
      outline: 'none',
    },
  };

  return {
    default: {
      ...baseStyles,
      border: `1px solid ${gray2}`,
      '& path': {
        fill: mainColor,
      },
      [breakpoints.up('sm')]: {
        '&:hover': {
          border: `1px solid ${mainColor}`,
        },
      },
    },
    white: {
      ...baseStyles,
      border: '1px solid rgba(255, 255, 255, 0.32)',
      '& path': {
        fill: whiteColor,
      },
      [breakpoints.up('sm')]: {
        '&:hover': {
          border: `1px solid ${whiteColor}`,
        },
      },
    },
    disabled: {
      opacity: 0.4,
      cursor: 'default',
      [breakpoints.up('sm')]: {
        '&:hover': {
          border: `1px solid ${gray2}`,
        },
      },
    },
    disabledWhite: {
      opacity: 0.32,
      cursor: 'default',
      [breakpoints.up('sm')]: {
        '&:hover': {
          border: '1px solid rgba(255, 255, 255, 0.32)',
        },
      },
    },
  };
};

/**
 * Button than contains only an icon.
 * Arrow icons can be obtained through props.
 * Custom icons can be passed as children.
 */
const IconButton = ({
  classes: classesProp,
  className: classNameProp,
  disabled,
  arrowLeft,
  arrowRight,
  white,
  children,
  test,
  ...other
}) => {
  const classes = test ? reduceClasses(classesProp) : classesProp;
  const renderIcon = () => {
    if (children) {
      return children;
    }
    if (arrowLeft) {
      return <ArrowLeft size={12} color={mainText} />;
    }
    if (arrowRight) {
      return <ArrowRight size={12} color={mainText} />;
    }
    return null;
  };

  return (
    <button
      className={classNames(
        {
          [classes.disabled]: disabled && !white,
          [classes.disabledWhite]: disabled && white,
          [classes.white]: white,
        },
        white ? classes.white : classes.default,
        classNameProp,
      )}
      disabled={disabled}
      {...other}
    >
      {renderIcon()}
    </button>
  );
};

IconButton.propTypes = {
  /** Optional children. Only rendered if no label was provided. */
  children: node,
  /** Classes from the styleSheet generated. */
  classes: shape({}).isRequired,
  /** Useful to extend the styleSheet applied to the button. */
  className: string,
  /** If true, the button will be disabled. */
  disabled: bool,
  /** White button used in dark backgrounds. */
  white: bool,
  /** Render with arrow left icon. */
  arrowLeft: bool,
  /** Render with arrow right icon. */
  arrowRight: bool,
  /** Useful for snapshot testing. */
  test: bool,
};

IconButton.defaultProps = {
  children: undefined,
  className: undefined,
  disabled: false,
  white: false,
  arrowLeft: false,
  arrowRight: false,
  test: false,
};

export default withStyles(styles)(IconButton);
